import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Loader, EmptyState } from "@/components/common/Loader";
import { CenterStatusBadge, TicketStatusBadge } from "@/components/common/Badges";
import ApprovalPanel from "@/pages/admin/center/ApprovalPanel";
import CenterInfoForm from "@/pages/admin/center/CenterInfoForm";
import { SpendingCard } from "@/components/common/Spending";
import CenterDocumentsAdmin from "@/pages/admin/center/CenterDocumentsAdmin";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, useFetch } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";

function CenterTickets({ centerId }) {
  const { data, loading } = useFetch(`/admin/tickets?center_id=${centerId}`);
  if (loading && !data) return <Loader />;
  if (!data?.length) return <EmptyState title="Nessun ticket" text="Questo centro non ha ancora aperto richieste." />;
  return (
    <div className="lux-card divide-y divide-warm/30">
      {data.map((t) => (
        <Link key={t.id} to={`/admin/ticket/${t.id}`} data-testid={`center-ticket-${t.code}`} className="flex flex-wrap items-center gap-4 px-5 py-4 hover:bg-ivory-deep/60">
          <span className="text-xs text-warm-text w-16">{t.code}</span>
          <span className="flex-1 min-w-[160px] text-sm truncate">{t.subject}</span>
          <TicketStatusBadge status={t.status} />
          <span className="text-xs text-warm-text">{fmtDate(t.created_at)}</span>
        </Link>
      ))}
    </div>
  );
}

function InternalNotes({ center, onSaved }) {
  const [notes, setNotes] = useState(center.internal_notes || "");
  const save = async () => {
    try {
      const r = await api.patch(`/admin/centers/${center.id}`, { internal_notes: notes });
      onSaved(r.data);
      toast.success("Note salvate");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="lux-card p-6 sm:p-8 space-y-4">
      <p className="text-sm text-warm-text">Visibili solo allo staff Skin Lovers.</p>
      <Textarea data-testid="center-internal-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={10} className="bg-ivory-deep rounded-xl" />
      <button data-testid="save-internal-notes-btn" onClick={save} className="btn-gold">Salva note</button>
    </div>
  );
}

export default function CenterDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { data: center, setData, loading } = useFetch(`/admin/centers/${id}`);
  const { data: lists } = useFetch("/admin/lists");
  if ((loading && !center) || !lists) return <Loader />;
  if (!center) return <EmptyState title="Centro non trovato" />;

  const toggleSuspend = async () => {
    const msg = center.status === "suspended" ? "Riattivare l'accesso di questo centro?" : "Sospendere questo centro? Non potrà più accedere all'app finché non lo riattiverai.";
    if (!window.confirm(msg)) return;
    try {
      const r = await api.post(`/admin/centers/${id}/${center.status === "suspended" ? "reactivate" : "suspend"}`);
      setData(r.data);
      toast.success(r.data.status === "suspended" ? "Centro sospeso" : "Centro riattivato");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const tabCls = "px-0 pb-2 tracking-[0.1em] sm:tracking-[0.16em] rounded-xl bg-transparent shadow-none data-[state=active]:shadow-none data-[state=active]:bg-transparent border-b-2 border-transparent data-[state=active]:border-gold text-[12px] uppercase tracking-[0.16em]";

  return (
    <div>
      <Link to="/admin/centri" className="btn-link">← Centri</Link>
      <div className="mt-6 mb-10 flex flex-col sm:flex-row sm:items-end justify-between gap-5 animate-fade-up">
        <div>
          <div className="flex items-center gap-4"><CenterStatusBadge status={center.status} />{center.client_type_name && <span className="eyebrow">{center.client_type_name}</span>}</div>
          <h1 data-testid="center-detail-name" className="text-3xl sm:text-5xl font-light mt-3">{center.center_name}</h1>
          <p className="text-sm text-warm-text mt-2">{center.company_name} · {center.city} ({center.province}) · iscritto il {fmtDate(center.created_at)}</p>
          <p data-testid="center-detail-registrant" className="text-xs text-warm-text mt-1">Richiesta compilata da: {center.registrant_role === "dipendente" ? "dipendente" : "titolare"} ({center.owner_first_name} {center.owner_last_name})</p>
          {center.approved_at && <p className="text-xs text-warm-text mt-1">Approvato da {center.approved_by_name} il {fmtDateTime(center.approved_at)}</p>}
        </div>
        {user.role === "superadmin" && ["approved", "suspended"].includes(center.status) && (
          <button data-testid="toggle-suspend-btn" onClick={toggleSuspend} className="btn-ghost">{center.status === "suspended" ? "Riattiva centro" : "Sospendi centro"}</button>
        )}
      </div>
      {["pending", "rejected"].includes(center.status) && <ApprovalPanel center={center} lists={lists} onDone={setData} />}
      {center.status === "rejected" && center.rejection_reason && <p className="lux-card p-4 text-sm mb-8">Motivazione del rifiuto: {center.rejection_reason}</p>}
      <Tabs defaultValue="info">
        <TabsList className="bg-transparent p-0 h-auto gap-x-5 gap-y-2 sm:gap-10 border-b border-warm/40 w-full justify-start rounded-xl flex-wrap mb-8">
          <TabsTrigger value="info" data-testid="center-tab-info" className={tabCls}>Scheda</TabsTrigger>
          <TabsTrigger value="spending" data-testid="center-tab-spending" className={tabCls}>Spesa</TabsTrigger>
          <TabsTrigger value="docs" data-testid="center-tab-documents" className={tabCls}>Documenti</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="center-tab-tickets" className={tabCls}>Ticket</TabsTrigger>
          <TabsTrigger value="notes" data-testid="center-tab-notes" className={tabCls}>Note interne</TabsTrigger>
        </TabsList>
        <TabsContent value="info"><CenterInfoForm key={center.updated_at} center={center} lists={lists} onSaved={setData} /></TabsContent>
        <TabsContent value="spending"><SpendingCard key={center.updated_at} endpoint={`/admin/centers/${center.id}/spending`} admin /></TabsContent>
        <TabsContent value="docs"><CenterDocumentsAdmin center={center} lists={lists} /></TabsContent>
        <TabsContent value="tickets"><CenterTickets centerId={center.id} /></TabsContent>
        <TabsContent value="notes"><InternalNotes center={center} onSaved={setData} /></TabsContent>
      </Tabs>
    </div>
  );
}
